import { useEffect, useRef, useState } from 'react'

import DashboardSettingsInputUpload
    from '../DashboardSettingsInputUpload/DashboardSettingsInputUpload'

import { getCssVar } from '../../../../utils/css'
import { createEvent } from '../../../../utils/dom'

export default function DashboardSettingsMaterialsInputs (props = {
    id: null,
    defaultValues: null,
    inputHiddenName: null,
    inputHiddenRef: null,
    onChange: null,
    disabled: null
}) {
    const id = props.id || 'company-materials'

    const inputHidden = useRef()

    const onRefCallback = (ref) => {
        inputHidden.current = ref

        if (props.inputHiddenRef
                && typeof props.inputHiddenRef === 'function')
            props.inputHiddenRef(ref)
    }

    const [state, setState] = useState({
        leanCanvas: Object.assign({
            url: null,
            public: false
        }, (props.defaultValues || {}).leanCanvas || {}),
        pitchDeck: Object.assign({
            url: null,
            public: false
        }, (props.defaultValues || {}).pitchDeck || {}),
        promotionalVideo: Object.assign({
            url: null,
            public: false
        }, (props.defaultValues || {}).promotionalVideo || {})
    })

    const getStateString = () => {
        let value = (inputHidden.current || {}).value || null

        try {
            value = JSON.stringify(state)
        }
        catch (e) {
            console.warn(e)
        }

        return value
    }

    const [value, setValue] = useState(getStateString())

    useEffect(() => {
        setValue(getStateString())
    }, [state])

    useEffect(() => {
        if (props.onChange
                && typeof props.onChange === 'function')
            props.onChange(value)

        if (!inputHidden.current)
            return

        const event = createEvent('change')

        inputHidden.current.dispatchEvent(event)

        Array.from(inputHidden.current.parentElement
            .querySelectorAll('[type=checkbox]:disabled'))
            .filter(el => el.checked)
            .forEach(el => {
                const valueObj = JSON.parse(value)

                const namespace = el.getAttribute('data-namespace')

                if (valueObj[namespace].url)
                    return

                el.checked = false
            })
    }, [value])

    const onDataNameInputChange = (e) => {
        const namespace = e.target.getAttribute('data-namespace')

        const name = e.target.getAttribute('data-name')

        if (!state[namespace] || !name)
            return

        const updateState = JSON.parse(JSON.stringify(state))

        const value = e.target.type === 'checkbox'
            ? e.target.checked
            : e.target.value || false

        if (updateState[namespace][name] === value)
            return

        updateState[namespace][name] = value

        setState(updateState)
    }

    return (
        <div
            className="dashboard-settings-materials-input margin-3-b"
            data-disabled={props.disabled}
        >
            <input
                type="hidden"
                name={props.inputHiddenName || 'company_materials'}
                value={value}
                ref={onRefCallback}
            />
            <fieldset
                className="margin-3-b pad-3-b"
                style={{
                    borderBottom: `1px solid ${getCssVar('--geyser')}`
                }}
            >
                <legend className="body font-weight-medium margin-1-b">Lean canvas</legend>
                <p
                    className="body-4 margin-b"
                    style={{
                        color: getCssVar('--slate')
                    }}
                >
                    The lean canvas is a widely used tool that helps ventures
                    quickly distill key assumptions about their venture into
                    concise, effective documentation. Within Catapult, sharing
                    your canvas can help advisors and community members
                    understand your proposition and approach and accelerate
                    your growth.
                </p>
                <DashboardSettingsInputUpload
                    id={`${id}-lean-canvas-upload`}
                    label={false}
                    type="pdf"
                    storageDirName="company-materials/lean-canvas"
                    data-namespace="leanCanvas"
                    data-name="url"
                    preview={state.leanCanvas.url}
                    disabled={props.disabled}
                    onChange={onDataNameInputChange}
                />
                <div className="form-field display-flex">
                    <input
                        id={`${id}-lean-canvas-public`}
                        type="checkbox"
                        data-namespace="leanCanvas"
                        data-name="public"
                        onChange={onDataNameInputChange}
                        defaultChecked={state.leanCanvas.url
                            ? state.leanCanvas.public
                            : false}
                        disabled={props.disabled
                            || !state.leanCanvas.url}
                    />
                    <div
                        className="margin-1-l"
                        style={{
                            marginTop: 1
                        }}
                    >
                        <label
                            htmlFor={`${id}-lean-canvas-public`}
                            className="body-4"
                            style={{
                                color: getCssVar('--slate')
                            }}
                        >
                            Publicly display on my profile to other entrepreneurs.
                        </label>
                    </div>
                </div>
            </fieldset>
            <fieldset
                className="margin-3-b pad-3-b"
                style={{
                    borderBottom: `1px solid ${getCssVar('--geyser')}`
                }}
            >
                <legend className="body font-weight-medium margin-1-b">Pitch deck</legend>
                <p
                    className="body-4 margin-b"
                    style={{
                        color: getCssVar('--slate')
                    }}
                >
                    Your pitch deck is a presentation used to succinctly and
                    compellingly orient audiences – advisors, partners, investors,
                    and more – to the mission, context, value, and plan of your venture.
                    Sharing your pitch deck within Catapult helps people quickly
                    understand your goals and needs, and add value to your journey.
                </p>
                <DashboardSettingsInputUpload
                    id={`${id}-pitch-deck-upload`}
                    label={false}
                    type="pdf"
                    storageDirName="company-materials/pitch-deck"
                    data-namespace="pitchDeck"
                    data-name="url"
                    preview={state.pitchDeck.url}
                    disabled={props.disabled}
                    onChange={onDataNameInputChange}
                />
                <div className="form-field display-flex">
                    <input
                        id={`${id}-pitch-deck-public`}
                        type="checkbox"
                        data-namespace="pitchDeck"
                        data-name="public"
                        onChange={onDataNameInputChange}
                        defaultChecked={state.pitchDeck.url
                            ? state.pitchDeck.public
                            : false}
                        disabled={props.disabled
                            || !state.pitchDeck.url}
                    />
                    <div
                        className="margin-1-l"
                        style={{
                            marginTop: 1
                        }}
                    >
                        <label
                            htmlFor={`${id}-pitch-deck-public`}
                            className="body-4"
                            style={{
                                color: getCssVar('--slate')
                            }}
                        >
                            Publicly display on my profile to other entrepreneurs.
                        </label>
                    </div>
                </div>
            </fieldset>
            <fieldset>
                <legend className="body font-weight-medium margin-1-b">Promotional Video</legend>
                <div className="form-field-floating margin-3-b">
                    <input
                        id={`${id}-promotional-video`}
                        type="url"
                        autoComplete="on"
                        autoCapitalize="none"
                        spellCheck="false"
                        pattern="^(https*:\/\/)*(www\.)*((?:(?:youtube|vimeo)\.com)|youtu\.be)\/.+"
                        data-url-scheme="https"
                        placeholder=" "
                        data-namespace="promotionalVideo"
                        data-name="url"
                        defaultValue={state.promotionalVideo.url}
                        readOnly={props.disabled}
                        onChange={onDataNameInputChange}
                    />
                    <label
                        htmlFor={`${id}-promotional-video`}
                    >
                        YouTube or Vimeo URL
                    </label>
                    <p
                        id={`${id}-promotional-video-invalid`}
                        role="alert"
                    >
                        A valid youtube.com or vimeo.com URL is required and must start with https://
                    </p>
                </div>
                <div className="form-field display-flex">
                    <input
                        id={`${id}-promotional-video-public`}
                        type="checkbox"
                        data-namespace="promotionalVideo"
                        data-name="public"
                        onChange={onDataNameInputChange}
                        defaultChecked={state.promotionalVideo.url
                            ? state.promotionalVideo.public
                            : false}
                        disabled={props.disabled
                            || !state.promotionalVideo.url}
                    />
                    <div
                        className="margin-1-l"
                        style={{
                            marginTop: 1
                        }}
                    >
                        <label
                            htmlFor={`${id}-promotional-video-public`}
                            className="body-4"
                            style={{
                                color: getCssVar('--slate')
                            }}
                        >
                            Publicly display on my profile to other entrepreneurs.
                        </label>
                    </div>
                </div>
            </fieldset>
        </div>
    )
}
